'use client';

import React, { useState } from 'react';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateAshtakavarga, AshtakavargaResult, CreateBirthProfileInput } from '@/lib/api/client';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { Grid, Sparkles, Award } from 'lucide-react';

const GRAHAS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

export default function AshtakavargaCalculatorPage() {
  const [result, setResult] = useState<AshtakavargaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGrahaTab, setSelectedGrahaTab] = useState<string>('Sun');

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateAshtakavarga(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Ashtakavarga bindu charts');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Ashtakavarga is a classical Parashari numerical assessment system allocating 337 total beneficent points (bindus) across the 12 zodiac signs from 7 planets plus Lagna. It measures house strength and transit receptivity.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I evaluate Ashtakavarga by computing 7 individual Bhinnashtakavarga charts (Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn) using Swiss Ephemeris (pyswisseph) under Lahiri Ayanamsha.
      </p>
      <p>
        The 7 individual charts are summed into the Sarvashtakavarga matrix (337 total bindus across 12 signs). Signs scoring 28+ bindus represent robust strength, while 30+ indicates powerful transit support.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "Bhinnashtakavarga vs Sarvashtakavarga",
      content: "Bhinnashtakavarga evaluates bindu scores for an individual planet (0 to 8 points per sign), while Sarvashtakavarga sums all 7 planets to produce total sign capacity (average 28 points)."
    },
    {
      title: "Evaluating Bindu Scores",
      content: "Houses with 28–30+ bindus easily absorb transit challenges and yield favorable outcomes during major transits, while houses under 25 bindus require extra caution and remedial care."
    },
    {
      title: "Predictive Application in Transits",
      content: "When a slow planet (like Jupiter or Saturn) transits a sign with high Sarvashtakavarga bindus, its positive significations manifest more smoothly and bountifully."
    }
  ];

  const faqs = [
    {
      q: "What is Ashtakavarga?",
      a: "Ashtakavarga is a numerical point-scoring system in Parashari Vedic astrology that quantifies planetary and house strength across 337 bindus."
    },
    {
      q: "What is a good Sarvashtakavarga bindu score?",
      a: "The average score per sign is 28 bindus. Scores of 28 to 30+ indicate strong, supportive houses, while 32+ indicates exceptional strength."
    },
    {
      q: "What is the maximum total bindu count in Sarvashtakavarga?",
      a: "The grand total across all 12 signs is exactly 337 bindus."
    },
    {
      q: "How does Ashtakavarga help with transit predictions?",
      a: "It acts as a filter: a planet transiting a sign with 30+ bindus yields strong positive results, even if the planet is naturally malefic."
    },
    {
      q: "Is this Ashtakavarga Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Generate your full Kundli with planetary longitudes and house divisions.",
      categoryBadge: "Core"
    },
    {
      title: "Shadbala Calculator",
      href: "/shadbala-calculator",
      description: "Compare Ashtakavarga bindu scores with 6-fold planetary strength.",
      categoryBadge: "Strength"
    },
    {
      title: "Transit Chart Calculator",
      href: "/transit-chart-calculator",
      description: "Overlay current transits onto your high-bindu Ashtakavarga signs.",
      categoryBadge: "Transit"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      <QuickCalculatorForm
        title="Ashtakavarga Calculator"
        subtitle="Calculate Parashari Bhinnashtakavarga (7 planetary charts) and Sarvashtakavarga (combined 12-sign bindu strength chart)."
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-8">
          {/* Main Sarvashtakavarga 12-Sign Summary Grid */}
          <div className="bg-astro-900 border border-amber-500/30 rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-800 pb-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-400 block">
                  Combined Total Bindu Strength
                </span>
                <h2 className="text-2xl font-serif font-bold text-gray-100 mt-0.5 flex items-center gap-2">
                  <Grid className="w-6 h-6 text-amber-400" />
                  <span>Sarvashtakavarga Summary Chart</span>
                </h2>
              </div>
              <div className="bg-astro-950 border border-amber-500/30 px-4 py-2 rounded-xl text-center">
                <span className="text-[10px] uppercase font-bold text-gray-400 block">Grand Total Bindus</span>
                <span className="text-2xl font-serif font-bold text-amber-400 font-mono">{result.grand_total}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {result.sign_breakdowns.map((sb) => (
                <div key={sb.sign_id} className="bg-astro-950 border border-gray-800 p-3 rounded-xl text-center">
                  <div className="text-xs text-gray-400 font-serif font-bold">{sb.sign_name}</div>
                  <div className="text-xl font-bold font-mono text-amber-300 mt-1">{sb.total_bindus}</div>
                  <div className="text-[10px] text-gray-500 font-mono">bindus</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-astro-900 border border-gray-800 rounded-2xl p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-serif font-bold text-gray-100">Bhinnashtakavarga (Individual Graha Charts)</h3>
            <p className="text-xs text-gray-400">Bhinnashtakavarga charts compute individual planet bindus (0–8 points) per sign.</p>
          </div>
        </div>
      )}

      <CalculatorPageContent
        calculatorName="Ashtakavarga Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Ashtakavarga Bindu Scores"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
